import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Cheque particulars captured for a CHEQUE tender / khata cheque collection (T-480). */
export interface ChequeForm {
  chequeNo: string;
  bankName: string;
  drawerName: string;
  chequeDate: string;
  dueDate: string;
}

/** Wire shape sent as `payments[].cheque` / `RecordPaymentDto.cheque`. */
export interface ChequePayload {
  chequeNo: string;
  bankName: string;
  drawerName?: string;
  chequeDate: string;
  dueDate?: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export const emptyChequeForm = (drawerName = ''): ChequeForm => ({
  chequeNo: '',
  bankName: '',
  drawerName,
  chequeDate: todayIso(),
  dueDate: '',
});

/** Cheque no + bank + cheque date are mandatory; drawer defaults to the customer; due date optional. */
export function isChequeFormComplete(f: ChequeForm): boolean {
  return f.chequeNo.trim() !== '' && f.bankName.trim() !== '' && f.chequeDate !== '';
}

export function chequeFormToPayload(f: ChequeForm): ChequePayload {
  return {
    chequeNo: f.chequeNo.trim(),
    bankName: f.bankName.trim(),
    drawerName: f.drawerName.trim() || undefined,
    chequeDate: f.chequeDate,
    dueDate: f.dueDate || undefined,
  };
}

interface ChequeFieldsProps {
  value: ChequeForm;
  onChange: (next: ChequeForm) => void;
}

export function ChequeFields({ value, onChange }: ChequeFieldsProps) {
  const { t } = useTranslation('pos');
  const set = (patch: Partial<ChequeForm>) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/20 p-3">
      <div>
        <Label className="text-xs">{t('chequeNo')} *</Label>
        <Input value={value.chequeNo} onChange={(e) => set({ chequeNo: e.target.value })} className="h-11" />
      </div>
      <div>
        <Label className="text-xs">{t('chequeBank')} *</Label>
        <Input value={value.bankName} onChange={(e) => set({ bankName: e.target.value })} className="h-11" />
      </div>
      <div>
        <Label className="text-xs">{t('chequeDrawer')}</Label>
        <Input value={value.drawerName} onChange={(e) => set({ drawerName: e.target.value })} className="h-11" />
      </div>
      <div>
        <Label className="text-xs">{t('chequeDate')} *</Label>
        <Input type="date" value={value.chequeDate} onChange={(e) => set({ chequeDate: e.target.value })} className="h-11" />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">{t('chequeDueDate')}</Label>
        <Input type="date" value={value.dueDate} onChange={(e) => set({ dueDate: e.target.value })} className="h-11" />
      </div>
    </div>
  );
}
